import { postWithStatusCheck } from '@/utils/api.js';
import { isIframe, iframeToken, iframeEndpoints, iframeTracking, iframeLeadToken, rememberIframeLeadToken } from '@/form/helpers/iframe-config.js';

// Used only when the server didn't tell us — see getSubmitTimeout below. These mirror
// App\Services\LeadSubmitTimeout's shipped defaults (US 510s, CA 240s) and must never be
// LOWER than the server's worst case, or the fallback path reintroduces the very abort
// this indirection exists to prevent. Erring high only means a longer spinner in a case
// that should not happen; erring low means a blank card. A PHP test
// (LeadSubmitTimeoutTest) fails if these drop below the derived values.
const FALLBACK_SUBMIT_TIMEOUT_MS = { US: 510000, CA: 240000 };

/**
 * How long to let a lead submit run before axios aborts it.
 *
 * The server computes this from CMI_SUBMIT_TIMEOUT_* and injects it per request
 * (window-inject.blade.php), because this ceiling MUST stay above the server's own
 * worst case: abort too early and the user gets a blank card, not an error, since the
 * form has already unmounted by the time the request dies. Deriving it server-side
 * means raising the backend timeout can never invert that order, and takes effect
 * without rebuilding this bundle.
 *
 * Falls back to the historical constants when the value is absent — a stale cached
 * bundle against an older server, or a page that doesn't inject window.App.
 */
function getSubmitTimeout(country) {
    const injected = typeof window !== 'undefined' ? window.App?.submit_timeout_ms : null;
    if (typeof injected === 'number' && Number.isFinite(injected) && injected > 0) {
        return injected;
    }
    return country === 'US' ? FALLBACK_SUBMIT_TIMEOUT_MS.US : FALLBACK_SUBMIT_TIMEOUT_MS.CA;
}

/**
 * A fresh id for THIS invocation of the submit call.
 *
 * The point is what it does NOT change on: when the browser's network stack re-POSTs a
 * request that went silent, the replay carries this same id. Two submits sharing an
 * attempt id therefore prove the duplicate came from the network, not from the form
 * firing twice. Minted per call — never at boot, never per page render.
 *
 * Travels as a header rather than a payload field so the raw request body stays
 * byte-identical: DuplicateSubmitGuard keys on a hash of that body, and a per-call
 * value inside it would make every re-click look like a fresh submission.
 *
 * crypto.randomUUID needs a secure context and is absent on older mobile Safari. This
 * is a diagnostic, so it must never throw into the submit path.
 */
function newAttemptId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch (e) { /* fall through to the non-crypto id */ }

    return 'a-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
}

const formFunctions =  {
    apiLogin: async function(payload) {
        return await postWithStatusCheck('/portal/member/login', payload);
    },
    apiGetQuiz: async function(payload) {
        return await postWithStatusCheck('/portal/member/quiz/get', payload);
    },
    apiGetVerified: async function(payload) {
        return await postWithStatusCheck('/portal/member/quiz', payload);
    },
    apiCreateAccount: async function(payload) {
        return await postWithStatusCheck('/portal/member', payload);
    },
    apiSubmitApplication: async function(payload) {
        // Iframe mode (window.IframeApp present): submit to the stateless endpoint
        // with the signed token instead of the session-backed member route. The
        // phone_token (if any) already rides along in `payload` from the iframe
        // SMS step. On the normal site isIframe() is false and nothing changes.
        let endpoint = '/portal/member/application';
        if (isIframe()) {
            endpoint = iframeEndpoints().submit;
            // Attribution passthrough: tracking params captured from the iframe
            // URL (utm_*, gclid, affiliate_sub*, ...) ride along, without
            // clobbering anything already collected in the form payload.
            payload = { ...iframeTracking(), ...payload, iframe_token: iframeToken() };
        }

        // Both submit routes get the server-derived ceiling rather than the 250000 this
        // branch used to hardcode: that constant is exactly what LeadSubmitTimeout exists
        // to remove, and the iframe endpoint reaches the same CMI call with the same worst
        // case, so it needs the same ordering guarantee. This works in iframe mode because
        // views/iframe/application.blade.php includes scripts.window-inject (so
        // window.App.submit_timeout_ms is present) and IframeApp.vue sets
        // projectBrandCountry -- the getSubmitTimeout() fallback is only for a stale bundle.
        const submitTimeout = getSubmitTimeout(payload?.projectBrandCountry);
        let response = await postWithStatusCheck(endpoint, payload, {
            timeout: submitTimeout,
            headers: { 'X-Submit-Attempt': newAttemptId() },
        });

        if (isIframe()) {
            rememberIframeLeadToken(response);
        }

        if(payload) {
            // === ecData dataLayer ===
            let isFromGoogle = payload.utm_source === 'google';

            // extract email&phone from payload to be able to push it to the dataLayer
            let email = payload.email ?? null;
            let phone = payload.phone_number ?? null;

            if(isFromGoogle && email && phone && typeof window !== 'undefined' && !!window.dataLayer) {
                if (!window._ecDataSubmitted) {
                    let ecData = {
                        "email": email,
                        "phone_number": '+1' + phone // include country code e.g. +15147774444
                    };

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        'event': 'ec_form_submit',
                        'ecData': ecData
                    });

                    window._ecDataSubmitted = true; // to do this only once
                    // console.log("ecData pushed:", ecData);
                }
                // else {
                //     console.log("ecData already pushed, skipping");
                // }
            }
        }

        return response;
    },
    apiGetScore: async function(payload) {
        return await postWithStatusCheck('/portal/member/score/get', payload);
    },
    // Iframe mode: the stateless endpoints identify the lead by the signed lead
    // token from the last submit; the uid in the payload is ignored there.
    apiStoreFlinks: async function(payload) {
        if (isIframe()) {
            return await postWithStatusCheck(iframeEndpoints().flinks, { ...payload, lead_token: iframeLeadToken() }, { timeout: 4000 });
        }
        return await postWithStatusCheck('/portal/flinks', payload, { timeout: 4000 });
    },
    apiHasFlinks: async function(payload) {
        if (isIframe()) {
            return await postWithStatusCheck(iframeEndpoints().flinksExists, { ...payload, lead_token: iframeLeadToken() }, { timeout: 4000 });
        }
        return await postWithStatusCheck('/portal/flinks/exists', payload, { timeout: 4000 });
    },
}

export { formFunctions }
